<template>
  <div>
    <div class="mb-8 grid grid-cols-2 gap-6 desktop:grid-cols-5">
      <VuelidateField :validation="v$.title">
        <template #default="{ error, state }">
          <FieldWrapper
            for="campaign-title"
            class="desktop:col-span-2"
            :label="t('Reviews.product.campaign.form.campaignName.input.label')"
            :label-hint="t('Reviews.product.campaign.form.campaignName.input.labelHint')"
            :error="error"
            :hint="t('Reviews.product.campaign.form.campaignName.input.hint', { min: NAME_MIN_LENGTH, max: NAME_MAX_LENGTH })"
          >
            <template #default="{ id }">
              <InputField
                :id="id"
                v-model="v$.title.$model"
                size="l"
                type="text"
                :maxlength="NAME_MAX_LENGTH"
                :state="state"
                :placeholder="t('Reviews.product.campaign.form.campaignName.input.placeholder')"
              />
            </template>
          </FieldWrapper>
        </template>
      </VuelidateField>
    </div>

    <div class="grid grid-cols-2 gap-6 desktop:grid-cols-5">
      <div class="flex gap-x-3 desktop:col-span-2">
        <VuelidateField :validation="v$.validFrom">
          <template #default="{ error, state }">
            <FieldWrapper for="campaign-valid-from" :label="t('Reviews.product.campaign.form.validFrom.input.label')" :error="error">
              <template #default="{ id }">
                <TooltipWrapper class="!w-full" top>
                  <template #trigger>
                    <DateField
                      :id="id"
                      v-model="v$.validFrom.$model"
                      size="l"
                      type="date"
                      :state="state"
                      :min="campaignStarted ? undefined : today"
                      :disabled="campaignStarted"
                    />
                  </template>
                  <template v-if="campaignStarted" #content>
                    {{ t('Reviews.product.campaign.form.validFrom.input.disabled.hint') }}
                  </template>
                </TooltipWrapper>
              </template>
            </FieldWrapper>
          </template>
        </VuelidateField>
        <VuelidateField :validation="v$.validTo">
          <template #default="{ error, state }">
            <FieldWrapper for="campaign-valid-to" :label="t('Reviews.product.campaign.form.validTo.input.label')" :error="error" optional>
              <template #default="{ id }">
                <DateField
                  :id="id"
                  v-model="v$.validTo.$model"
                  size="l"
                  type="date"
                  :state="state"
                  :min="minValidTo"
                  :max="maxValidToDate"
                />
              </template>
            </FieldWrapper>
          </template>
        </VuelidateField>
      </div>
    </div>
    <div
      v-if="budgetEndDate"
      class="mb-10 w-1/2 pr-3 txt-label-m text-subtlest desktop:w-2/5"
      v-html="t('Reviews.product.campaign.form.validTo.hint', { date: budgetEndDateLong })"
    />
    <div class="mb-3 flex items-center gap-2 txt-headline-xl text-main">
      {{ t('Reviews.product.campaign.form.incentive.label') }}
    </div>
    <div class="mb-8 grid grid-cols-2 gap-6 txt-body-m desktop:grid-cols-5">
      <MarkdownContent
        class="flex flex-col gap-3 desktop:col-span-2"
        :unstyled="true"
        :md="t('Reviews.product.campaign.editForm.incentive.description').toString()"
      />
    </div>
    <div class="mb-8 grid grid-cols-2 gap-x-6 desktop:grid-cols-5">
      <div class="desktop:col-span-2">
        <FieldWrapper for="campaign-incentive-selection" :label="t('Reviews.product.campaign.form.incentive.input.label')">
          <template #default="{ id }">
            <fieldset :id="id" class="flex flex-col gap-x-6 gap-y-2 desktop:col-span-2">
              <TooltipWrapper class="!w-full" top>
                <template #trigger>
                  <RadioCard disabled>
                    <span>{{ incentiveRadioCardOption }}</span>
                    <template v-if="campaign.incentivizationType === IncentivizationCampaignIncentivizationTypeEnum.Amazon" #nested>
                      <FieldWrapper for="campaign-incentive-selection-inline">
                        <template #default="{ id: inputId }">
                          <SelectField :id="inputId" :model-value="campaign.amount || 0" disabled>
                            <option v-for="option in amazonAmountOptions" :key="option.value" :value="option.value">
                              {{ option.label }}
                            </option>
                          </SelectField>
                        </template>
                      </FieldWrapper>
                    </template>
                  </RadioCard>
                </template>
                <template #content>
                  {{ t('Reviews.product.campaign.form.incentive.input.disabled.hint') }}
                </template>
              </TooltipWrapper>
            </fieldset>
          </template>
        </FieldWrapper>
      </div>
    </div>
    <div v-if="campaign.incentivizationType" class="mb-8 grid grid-cols-2 gap-6 desktop:grid-cols-5">
      <FieldWrapper
        for="campaign-limit-type-selection"
        class="desktop:col-span-2"
        :label="t('Reviews.product.campaign.form.limitType.input.label')"
      >
        <template #default="{ id }">
          <fieldset :id="id" class="flex flex-col gap-x-6 gap-y-2 desktop:col-span-2">
            <TooltipWrapper class="!w-full" top>
              <template #trigger>
                <RadioCard disabled>
                  <span>{{ limitTypeRadioCardOption }}</span>
                  <template v-if="campaign.incentivizationsLimit" #nested>
                    <FieldWrapper for="campaign-limit-type-selection-inline">
                      <template #default="{ id: inputId }">
                        <InputField
                          v-if="campaign.limitType === IncentivizationCampaignLimitTypeEnum.Incentivizations"
                          :id="inputId"
                          :model-value="campaign.incentivizationsLimit"
                          disabled
                        >
                          <template #append><span class="text-subtlest">reviews</span></template>
                        </InputField>
                        <InputField v-else :id="inputId" :model-value="incentivizationsBudgetLimit" disabled>
                          <template #prepend>
                            <EuroIcon class="lucide-icon-s text-subtlest" />
                          </template>
                        </InputField>
                      </template>
                    </FieldWrapper>
                  </template>
                </RadioCard>
              </template>
              <template #content>
                {{ t('Reviews.product.campaign.form.incentive.input.disabled.hint') }}
              </template>
            </TooltipWrapper>
          </fieldset>
        </template>
      </FieldWrapper>
      <CampaignLimitPreviewBox
        v-if="campaign.limitType && campaign.incentivizationType"
        class="desktop:col-span-2"
        :incentive-amount="campaign.amount || 0"
        :incentivization-type="campaign.incentivizationType"
        :incentivizations-limit="campaign.incentivizationsLimit || 0"
        :budget-limit="incentivizationsBudgetLimit"
        :variant="campaign.limitType"
      />
    </div>
    <div class="mb-3 txt-headline-xl text-main">{{ t('Reviews.product.campaign.form.inviteMessage.label') }}</div>
    <div class="mb-8 grid grid-cols-2 gap-6 txt-body-m desktop:grid-cols-5">
      <div ref="markdownContentRef" class="flex flex-col gap-3 desktop:col-span-3">
        <MarkdownContent
          :unstyled="true"
          :md="t('Reviews.product.campaign.editForm.personalization.description', { link: campaignOverviewLink }).toString()"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  TooltipWrapper,
  FieldWrapper,
  InputField,
  DateField,
  MarkdownContent,
  RadioCard,
  useCurrencyFormatter,
  SelectField,
} from '@ramp106/omrjs-core-ui'
import { EuroIcon } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate, type ValidationArgs } from '@vuelidate/core'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useMarkdownLinkNavigation } from '@/composables/useMarkdownLinkNavigation'
import { useReviewsCampaignFormOptions, NAME_MIN_LENGTH, NAME_MAX_LENGTH } from '@/composables/useReviewsCampaignFormOptions'
import {
  IncentivizationCampaignIncentivizationTypeEnum,
  IncentivizationCampaignLimitTypeEnum,
  type ProductData,
  type Campaign,
} from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'
import { maxLength, minLength, required, dateAfter, dateAfterOrEqual } from '@/services/validators.ts'
import VuelidateField from '@/ui/VuelidateField.vue'

import CampaignLimitPreviewBox from './CampaignLimitPreviewBox.vue'
import type { CampaignEditFormData } from './types'

const props = defineProps<{
  initialData: CampaignEditFormData
  campaign: Pick<Campaign, 'id' | 'amount' | 'incentivizationType' | 'incentivizationsLimit' | 'limitType'>
  product: ProductData
  campaignStarted: boolean
}>()

const emit = defineEmits<{
  (e: 'change', value: CampaignEditFormData): void
}>()

const { t } = useI18n()
const router = useRouter()
const { formatCurrency } = useCurrencyFormatter()

const { today, maxValidToDate, budgetEndDate, budgetEndDateLong, amazonAmountOptions, wunschFormattedValue, amazonFormattedValue } =
  useReviewsCampaignFormOptions(props.product)

const incentiveRadioCardOption = computed(() => {
  if (props.campaign.incentivizationType === IncentivizationCampaignIncentivizationTypeEnum.Wunsch) {
    return t('Reviews.product.campaign.form.incentiveSelection.input.wunsch', { value: wunschFormattedValue.value })
  }
  if (props.campaign.incentivizationType === IncentivizationCampaignIncentivizationTypeEnum.Amazon) {
    return t('Reviews.product.campaign.form.incentiveSelection.input.amazon', { value: amazonFormattedValue.value })
  }
  return t('Reviews.product.campaign.form.incentiveSelection.input.noIncentive')
})

const limitTypeRadioCardOption = computed(() => {
  if (!props.campaign.incentivizationsLimit) {
    return t('Reviews.product.campaign.form.limitType.input.option.useRemainingBudget', {
      amount: formatCurrency(props.product.currentBudget?.remainingValue || 0),
    })
  }
  if (props.campaign.limitType === IncentivizationCampaignLimitTypeEnum.Budget) {
    return t('Reviews.product.campaign.form.limitType.input.option.setMaxBudget')
  }
  return t('Reviews.product.campaign.form.limitType.input.option.setByReviews')
})

const incentivizationsBudgetLimit = computed(() => {
  if (!props.campaign.incentivizationsLimit || !props.campaign.amount) return 0
  return props.campaign.incentivizationsLimit * props.campaign.amount
})

const campaignOverviewLink = computed(() => {
  return router.resolve({
    name: ReviewsRouteName.ReviewsProductCampaignOverview,
    params: { productId: props.product.id, campaignId: props.campaign.id },
  }).path
})

const markdownContentRef = ref<HTMLElement>()

useMarkdownLinkNavigation(markdownContentRef, campaignOverviewLink)

const formData = reactive<CampaignEditFormData>({
  ...props.initialData,
})

const minValidTo = computed(() => {
  if (!formData.validFrom) return today
  return formData.validFrom > today ? formData.validFrom : today
})

const rules = computed<ValidationArgs>(() => ({
  title: {
    required,
    minLength: minLength(NAME_MIN_LENGTH),
    maxLength: maxLength(NAME_MAX_LENGTH),
  },
  validFrom: {
    required,
    ...(props.campaignStarted ? {} : { dateAfterOrEqual: dateAfterOrEqual(today) }),
  },
  validTo: {
    dateAfter: dateAfter(minValidTo.value),
  },
}))

const $externalResults = ref<Partial<Record<keyof CampaignEditFormData, string>>>({})

const v$ = useVuelidate(rules, formData, { $externalResults })

function updateExternalErrors(errors: Partial<Record<keyof CampaignEditFormData, string>>) {
  $externalResults.value = errors
}

defineExpose({
  updateExternalErrors,
  validate: () => v$.value.$validate(),
  isValid: computed(() => !v$.value.$invalid),
  clearExternalErrors: () => v$.value.$clearExternalResults(),
})

watch(
  formData,
  () => {
    emit('change', { ...formData })
  },
  { deep: true },
)
</script>
